import {
  Component,
  OnInit,
  Input,
  Output,
  EventEmitter,
  ViewChild,
  ElementRef,
  TemplateRef,
  OnChanges,
} from "@angular/core";
import { keyCodes } from "../../models/key-codes.model";
import { trigger, transition, animate, style } from "@angular/animations";
import { FormsModule } from "@angular/forms";
import { CommonModule } from "@angular/common";

@Component({
  selector: "auto-complete",
  imports: [FormsModule, CommonModule],
  standalone: true,

  animations: [
    trigger("fadeIn", [
      transition(":enter", [
        style({ transform: "translateY(10%)", opacity: 0 }),
        animate(
          "200ms ease-out",
          style({ transform: "translateY(0%)", opacity: 1 })
        ),
      ]),
      transition(":leave", [
        style({ opacity: 1 }),
        animate("200ms ease-out", style({ opacity: 0 })),
      ]),
    ]),
  ],
  template: `
    <div (mouseenter)="mouseIn = true" (mouseleave)="mouseIn = false">
      <div class="l-align-y">
        <div class="l-cell-spread-x t-input-search-wrap">
          <input
            #autoCompleteInput
            class="t-input t-input--search"
            [disabled]="!(data && data.length)"
            [attr.placeholder]="placeholder"
            [(ngModel)]="query"
            (focus)="showResults = true"
            (blur)="clearResultsPanel($event)"
            (click)="showResults = true; initAutoComplete($event)"
            (keyup)="showResults = true; initAutoComplete($event)"
            (keydown)="checkSelectAll($event)"
          />
        </div>

        <button
          *ngIf="isItemsSelected()"
          class="t-button-close"
          (click)="unselect()"
          (blur)="showResults = false"
        ></button>

        <ng-container *ngTemplateOutlet="afterSearchField"></ng-container>
      </div>

      <div *ngIf="showResults && results && results.length" class="h-null">
        <div [@fadeIn] class="t-autoComplete-panel" #autoCompleteListHold>
          <ul class="norm-ul t-autoComplete-list">
            <li
              *ngFor="let item of results"
              class="t-autoComplete-item"
              (click)="chooseSingleItem(item)"
              [ngClass]="{
                'is-selected': item.selected,
                'is-focused': item.focused
              }"
            >
              <span *ngFor="let value of getValues(item)">
                {{ item[value] }}
              </span>
            </li>
          </ul>
        </div>
      </div>
    </div>
  `,
})
export class AutoCompleteComponent implements OnInit, OnChanges {
  @Input() data: Object[];
  @Input() displayKeys: string[];
  @Input() query: string;
  @Input() singleSelection: boolean;
  @Input() initialPlaceholder: string;
  @Input() afterSearchField: TemplateRef<any>;
  @Output() onQuery = new EventEmitter<Object[] | Object>();
  @ViewChild("autoCompleteListHold", { static: false })
  autoCompleteListHold: ElementRef;
  @ViewChild("autoCompleteInput", { static: false })
  autoCompleteInput: ElementRef;

  keyCodes = keyCodes;
  results: any[] = [];
  placeholder: string;
  rangeFrom: number = 0;
  selectedByCtrl: number[] = [];
  showResults: boolean = false;
  counter: number = -1;
  mouseIn: boolean;
  isSelectAll: boolean;
  focusedItem: Object;
  selectedItem: Object;

  constructor() {}

  ngOnInit() {
    this.placeholder = this.initialPlaceholder;
  }

  ngAfterViewInit() {
    document.addEventListener("mouseup", () => {
      if (!this.mouseIn) this.showResults = false;
    });
  }

  ngOnChanges() {}

  public initAutoComplete(event: any) {
    if (!this.showResults) return;

    const queryString: string = event.target.value;

    this.results = this.data.filter((item) => {
      const stringified = this.stringifyValues(item);
      const searchQuery = this.comparable(queryString);
      return stringified.indexOf(searchQuery) !== -1;
    });
    event.type === "keyup" && this.arrowNav(event);
  }

  public getValues(item) {
    if (this.displayKeys && this.displayKeys.length) {
      return this.displayKeys;
    } else {
      return Object.keys(item).filter(
        (key) => "string,number".indexOf(typeof item[key]) !== -1
      );
    }
  }

  public chooseSingleItem(item) {
    this.selectedItem = item;
    if (this.singleSelection) {
      this.onQuery.emit(item);
    } else {
      this.onQuery.emit([item]);
    }
    this.showResults = false;
    this.renderPlaceholderString(item);
  }

  public checkSelectAll(event) {
    if ((event.ctrlKey || event.metaKey) && event.keyCode === this.keyCodes.a) {
      this.isSelectAll = true;
    }
  }

  public unselect(initial: boolean = false) {
    this.showResults = false;
    this.selectedByCtrl = [];
    this.query = this.placeholder = null;
    this.counter = -1;
    this.highlightSelectedItems();
    this.results = [];
    this.selectedItem = null;
    this.placeholder = this.initialPlaceholder;
    !initial && this.onQuery.emit(null);
  }

  public clearResultsPanel($event) {
    if (!this.mouseIn) this.showResults = false;
  }

  private stringifyValues(objItem: Object): string {
    let str: string;
    Object.keys(objItem)
      .filter((key) => "string number".indexOf(typeof objItem[key]) !== -1)
      .forEach((key) => {
        str += " " + this.comparable(objItem[key]);
      });

    return str;
  }

  private comparable(str: string) {
    if ("string number".indexOf(typeof str) === -1) return "";
    let _str: string = typeof str === "number" ? "" + str : str;
    _str = _str
      .replace(/[-]/g, " ")
      .replace(/\s\s+/g, " ")
      .trim()
      .toLowerCase();
    return _str;
  }

  private populateMultiSelect() {
    if (this.singleSelection) return;
    let indexOfCounter = this.selectedByCtrl.indexOf(this.counter);
    if (indexOfCounter === -1) {
      this.selectedByCtrl.push(this.counter);
    } else {
      this.selectedByCtrl.splice(indexOfCounter, 1);
    }
  }

  private selectAll() {
    this.results.forEach((el, i) => this.selectedByCtrl.push(i));
  }

  private arrowNav(event) {
    if (
      event.keyCode === this.keyCodes.arrowDown &&
      this.counter < this.results.length - 1
    ) {
      if (event.shiftKey) {
        this.populateMultiSelect();
      }
      this.counter++;
    }

    if (event.keyCode === this.keyCodes.arrowUp && this.counter >= 0) {
      this.counter--;
      if (event.shiftKey) {
        this.populateMultiSelect();
      }
    }

    if (
      [this.keyCodes.ctrl, this.keyCodes.meta].indexOf(event.keyCode) !== -1
    ) {
      this.populateMultiSelect();
    }

    if (this.isSelectAll) {
      this.selectAll();
      this.isSelectAll = false;
    }

    if (Object.values(this.keyCodes).join().indexOf(event.keyCode) === -1) {
      this.counter = -1;
      this.selectedByCtrl = [];
    }

    if (event.keyCode === this.keyCodes.escape) {
      this.showResults = false;
      this.counter = -1;
      this.selectedByCtrl = [];
    }

    if (event.keyCode === this.keyCodes.enter) {
      const selected = this.results.filter((el) => !!el["selected"]);
      this.renderPlaceholderString(selected);
      if (this.singleSelection) {
        this.onQuery.emit(selected[0]);
      } else {
        this.onQuery.emit(selected);
      }
      this.showResults = false;
      if (this.results.length === 0)
        this.renderPlaceholderString(this.results[0]);
    }

    if (
      event.keyCode === this.keyCodes.enter &&
      !this.results.find((e) => e["selected"])
    ) {
      const selected = this.results;
      this.renderPlaceholderString(selected);
      this.onQuery.emit(selected);
      this.showResults = false;
    }

    this.highlightSelectedItems();

    this.scrollPanel(event);
  }

  private highlightSelectedItems() {
    this.results &&
      this.results.forEach((item, index) => {
        if (
          (index >= this.counter && index <= this.counter + this.rangeFrom) ||
          this.selectedByCtrl.indexOf(index) !== -1
        ) {
          item["selected"] = true;
        } else {
          delete item["selected"];
        }

        if (index === this.counter) {
          item["focused"] = true;
        } else {
          delete item["focused"];
        }

        this.focusedItem = item["focused"] || null;
      });
  }

  private renderPlaceholderString(item: any) {
    if (!item) return this.initialPlaceholder;
    let _item = new Array().concat(item);
    const displyItems: number = 3;
    const ellipsis: string =
      _item.length > displyItems
        ? `...   (+${_item.length - displyItems} )`
        : "";
    this.query = null;
    this.autoCompleteInput.nativeElement.blur();
    this.placeholder =
      _item
        .filter((it, index) => index < displyItems)
        .map((it) => {
          return this.getValues(it)
            .map((k) => it[k])
            .join(", ");
        })
        .join(" | ") + ellipsis;
  }

  private scrollPanel(event) {
    if (!this.autoCompleteListHold) return;
    const panel: HTMLElement = this.autoCompleteListHold.nativeElement;
    const focused: HTMLElement = panel.querySelector(".is-focused");
    if (!focused || !panel) return;
    const panelPosBottom = panel.getBoundingClientRect().bottom;
    const liPosBottom = focused.getBoundingClientRect().bottom;
    const panelPosTop = panel.getBoundingClientRect().top;
    const liPosTop = focused.previousElementSibling
      ? focused.previousElementSibling.getBoundingClientRect().top
      : focused.getBoundingClientRect().top;

    if (
      liPosBottom > panelPosBottom - focused.offsetHeight &&
      event.keyCode === this.keyCodes.arrowDown
    ) {
      panel.scrollTop += panel.offsetHeight - focused.offsetHeight;
    }
    if (liPosTop < panelPosTop && event.keyCode === this.keyCodes.arrowUp) {
      panel.scrollTop -= panel.offsetHeight - focused.offsetHeight;
    }
  }

  public isItemsSelected() {
    if (!this.selectedByCtrl || !this.results) {
      return false;
    }

    return (
      this.selectedByCtrl.length || this.results.length || this.selectedItem
    );
  }
}
